/**
 * Outage-map initial framing (R-P3-2).
 *
 * The map's three-second job is "is MY area affected?" — so it must open on the
 * customer's context, never on open ocean. The mobile map opened on a hardcoded
 * Providenciales centre at a fixed zoom, which is only coincidentally right for
 * Provo personas and simply wrong for anyone else.
 *
 * Shared rather than mobile-local per the cross-surface parity contract: camera
 * framing is domain truth (which outages are you shown, relative to where you
 * live), not presentation. Web's OutageMap can adopt the same frame — see the
 * note in outageMapFrame.
 *
 * NOTE ON THE RULING'S PREMISE: R-P3-2 says "fit-bounds of {the persona's
 * service-address coordinates} ∪ {all active-outage markers}". Personas carry NO
 * coordinates on disk — `serviceAddress` is `{ line1, district, island }` only.
 * The customer's location is therefore derived from the polygon of their
 * `serviceCircuitId` in CIRCUIT_POLYGONS_GEOJSON, which is real fixture data and
 * is arguably the better anchor anyway: the question is about the feeder area,
 * not a rooftop.
 */

/** [longitude, latitude] — MapLibre's coordinate order on both surfaces. */
export type LngLat = [number, number];

export interface LngLatBounds {
  sw: LngLat;
  ne: LngLat;
}

/**
 * Minimum span, in degrees, for a frame built from a single point or a very
 * small polygon. Without it the camera zooms to its maximum on a lone marker and
 * the customer loses all surrounding context. ~0.02° ≈ 2 km at this latitude,
 * which keeps a neighbourhood in view.
 */
export const MIN_FRAME_SPAN_DEG = 0.02;

/** Bounding box of a set of points. Null for an empty set. */
export function boundsOfPoints(points: readonly LngLat[]): LngLatBounds | null {
  if (points.length === 0) return null;

  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  for (const [lng, lat] of points) {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  }

  if (!Number.isFinite(minLng) || !Number.isFinite(minLat)) return null;
  return { sw: [minLng, minLat], ne: [maxLng, maxLat] };
}

/** Smallest box containing both. Either may be null. */
export function unionBounds(
  a: LngLatBounds | null,
  b: LngLatBounds | null,
): LngLatBounds | null {
  if (!a) return b;
  if (!b) return a;
  return {
    sw: [Math.min(a.sw[0], b.sw[0]), Math.min(a.sw[1], b.sw[1])],
    ne: [Math.max(a.ne[0], b.ne[0]), Math.max(a.ne[1], b.ne[1])],
  };
}

/**
 * Grow a box symmetrically until it spans at least `minSpan` on both axes.
 * A box already larger than the minimum is returned unchanged.
 */
export function expandBounds(
  bounds: LngLatBounds,
  minSpan: number = MIN_FRAME_SPAN_DEG,
): LngLatBounds {
  const lngSpan = bounds.ne[0] - bounds.sw[0];
  const latSpan = bounds.ne[1] - bounds.sw[1];
  const padLng = Math.max(0, (minSpan - lngSpan) / 2);
  const padLat = Math.max(0, (minSpan - latSpan) / 2);

  return {
    sw: [bounds.sw[0] - padLng, bounds.sw[1] - padLat],
    ne: [bounds.ne[0] + padLng, bounds.ne[1] + padLat],
  };
}

/** Minimal GeoJSON shapes this module reads — avoids a geojson type dependency. */
interface PolygonFeature {
  properties?: Record<string, unknown> | undefined;
  geometry?: { type?: string; coordinates?: unknown } | undefined;
}

/** Every [lng, lat] pair in an arbitrarily-nested coordinate array. */
function flattenPositions(coords: unknown, out: LngLat[] = []): LngLat[] {
  if (!Array.isArray(coords)) return out;
  if (
    coords.length >= 2 &&
    typeof coords[0] === "number" &&
    typeof coords[1] === "number"
  ) {
    out.push([coords[0], coords[1]]);
    return out;
  }
  for (const child of coords) flattenPositions(child, out);
  return out;
}

/** Bounds of the first feature whose `properties[key]` equals `value`. */
export function featureBoundsByProperty(
  features: readonly PolygonFeature[],
  key: string,
  value: unknown,
): LngLatBounds | null {
  if (value == null) return null;
  const match = features.find((f) => f.properties?.[key] === value);
  if (!match) return null;
  return boundsOfPoints(flattenPositions(match.geometry?.coordinates));
}

/** Web-Mercator y for a latitude, in the projection's natural units. */
function mercatorY(lat: number): number {
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  return Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360));
}

export interface ViewportDp {
  width: number;
  height: number;
}

export interface CameraStop {
  center: LngLat;
  zoom: number;
}

/** Tile size the zoom scale is defined against. */
const TILE = 256;

/**
 * Convert a frame to `centerCoordinate` + `zoomLevel`.
 *
 * WHY THIS EXISTS rather than handing bounds to the map component: MapLibre RN's
 * declarative `bounds` prop takes padding via a shape its own typings label
 * "backwards compatibility", and passing it produced a map that rendered nothing
 * on a physical device (2026-07-24) — no errors, valid bounds, black at every pan
 * position. Scalar centre+zoom is the API the screen used before and is trivially
 * unit-testable, so the framing decision stays here in shared code while the
 * component keeps to the boring path.
 *
 * `padding` shrinks the usable viewport so content clears overlay chrome.
 */
export function boundsToCamera(
  bounds: LngLatBounds,
  viewport: ViewportDp,
  padding: { top?: number; bottom?: number; left?: number; right?: number } = {},
  limits: { minZoom?: number; maxZoom?: number } = {},
): CameraStop {
  const center: LngLat = [
    (bounds.sw[0] + bounds.ne[0]) / 2,
    (bounds.sw[1] + bounds.ne[1]) / 2,
  ];

  // Usable viewport after overlays. Floor at 1dp so padding can never produce a
  // zero or negative extent (which would send zoom to ±Infinity).
  const usableW = Math.max(1, viewport.width - (padding.left ?? 0) - (padding.right ?? 0));
  const usableH = Math.max(1, viewport.height - (padding.top ?? 0) - (padding.bottom ?? 0));

  const lngSpan = Math.abs(bounds.ne[0] - bounds.sw[0]);
  const latSpan = Math.abs(mercatorY(bounds.ne[1]) - mercatorY(bounds.sw[1]));

  const zoomX = lngSpan > 0 ? Math.log2((usableW / TILE) * (360 / lngSpan)) : Infinity;
  const zoomY = latSpan > 0 ? Math.log2((usableH / TILE) * ((2 * Math.PI) / latSpan)) : Infinity;

  const min = limits.minZoom ?? 3;
  const max = limits.maxZoom ?? 16;
  const fit = Math.min(zoomX, zoomY);
  const zoom = Number.isFinite(fit) ? Math.max(min, Math.min(max, fit)) : max;

  // ASYMMETRIC PADDING SHIFTS THE VISUAL CENTRE. Fitting to the padded extent is
  // only half the job: with top 140 / bottom 96 the usable area's centre sits
  // (top-bottom)/2 = 22dp BELOW the viewport centre, so centring on the raw
  // midpoint renders everything 22dp too high — pushing content that should be
  // in view up underneath the banner. Observed on device: the outage marker
  // sitting exactly on the frame's NE corner was not visible.
  //
  // Correct by moving the camera centre by that offset. Screen y grows downward,
  // so shifting content DOWN means moving the centre NORTH; likewise extra left
  // padding moves the centre WEST.
  const worldPx = TILE * Math.pow(2, zoom);
  const lngPerPx = 360 / worldPx;
  const latPerPx = lngPerPx * Math.cos((center[1] * Math.PI) / 180);

  const dxPx = ((padding.left ?? 0) - (padding.right ?? 0)) / 2;
  const dyPx = ((padding.top ?? 0) - (padding.bottom ?? 0)) / 2;

  return {
    center: [center[0] - dxPx * lngPerPx, center[1] + dyPx * latPerPx],
    zoom,
  };
}

export interface OutageMapFrameOptions {
  /** The active persona's `serviceCircuitId`, if any. */
  circuitId?: string | undefined;
  /** Circuit polygons — CIRCUIT_POLYGONS_GEOJSON.features. */
  circuitFeatures?: readonly PolygonFeature[] | undefined;
  /** Markers to keep in frame. Pass only the ACTIVE ones (see countActiveOutages). */
  markers?: readonly LngLat[] | undefined;
  /** Last-resort centre when nothing else resolves. */
  fallbackCenter: LngLat;
}

/**
 * The initial camera frame: the customer's feeder area ∪ every active-outage
 * marker, never smaller than MIN_FRAME_SPAN_DEG.
 *
 * Degrades in the order the customer's question demands:
 *   circuit + markers -> both in view (the answer is "yes/no, and where")
 *   circuit only      -> your area (no active outages to show)
 *   markers only      -> the network's outages (persona has no mapped circuit)
 *   neither           -> fallbackCenter
 *
 * Returns bounds rather than a centre+zoom so the CALLER owns padding — which
 * matters because the outage map's overlays (report banner, legend, CTA) cover
 * real estate, and a marker hidden under an overlay is just as invisible as one
 * outside the frame.
 */
export function outageMapFrame(opts: OutageMapFrameOptions): LngLatBounds {
  const circuit = featureBoundsByProperty(
    opts.circuitFeatures ?? [],
    "circuitId",
    opts.circuitId,
  );
  const markers = boundsOfPoints(opts.markers ?? []);
  const combined = unionBounds(circuit, markers);

  if (!combined) {
    return expandBounds({ sw: opts.fallbackCenter, ne: opts.fallbackCenter });
  }
  return expandBounds(combined);
}
